/**
 * The `/dashboard` empty-record gate (issue #760, #702's own defect).
 *
 * Kept in its own pure-JS module — no React, no `newUi()` call baked in —
 * specifically so it is unit-testable directly, the same reason
 * `lib/chromelessRoutes.js` gives for `isChromelessRoute`: a plain function
 * of its inputs is a fact a test can pin without mounting a component (this
 * repo's own convention — see `components/kit/EmptyState.test.js`'s
 * header), and `pages/Dashboard.jsx` calls it with the real `newUi("record")`
 * read at render time rather than duplicating the arithmetic inline.
 *
 * THE DEFECT (#702): the old gate was
 *   `const isEmpty = filterActive && summary && summary.matches === 0;`
 * — a brand-new account has no filter, so `isEmpty` was always false for
 * one, and the four stat tiles rendered real zeros instead of the designed
 * empty state. The corrected formula is "the summary is in, and it says
 * zero matches" — full stop, no filter requirement.
 *
 * WHY THE FLAG STILL MATTERS. Epic #749's own rule is "presentation is
 * rewritten, behaviour is kept": a screen not yet redrawn stays exactly as
 * shipped. The flag-off leg therefore keeps the OLD (buggy) formula,
 * byte-for-byte, because #760 has not redrawn that leg's screen — fixing
 * the formula unconditionally would be a behaviour change with no flag
 * gating it, which is exactly what this wave does not do anywhere else.
 *
 * TWO DIFFERENT EMPTY STATES, ONE GATE. A filter that matches nothing and
 * an account that has played nothing are different states and must read
 * differently (issue #760 § 1):
 *   - no matches at all, no filter -> `showEmptyRecord` -> the redrawn
 *     empty record (`RecordEmptyScreen`).
 *   - a filter matches nothing -> `isEmpty` alone -> the existing plain
 *     "no matches for this filter" card, unchanged shape.
 *   - matches present -> neither -> the ordinary populated dashboard,
 *     unchanged (the hard rule this slice must not touch).
 *
 * PR #846 fix round, B3 (#465) — a THIRD outcome, `recordLoading`: the
 * gate literally cannot answer `isEmpty` before `summary` has arrived (or
 * failed), and while flag-on, unfiltered players still saw the OLD
 * populated dashboard — bell and all — during that window. The instant
 * the summary landed and the gate resolved `showEmptyRecord`, the page
 * swapped to the redrawn screen's OWN, different bell — a notification
 * panel opened in the old bell's brief window had already stamped the
 * read watermark, then unmounted from under itself (found as journey 20
 * failing 3/3 alongside journey 08). `recordLoading` names that window
 * explicitly so `pages/Dashboard.jsx` can render a neutral frame (no
 * bell, no menus, nothing to lose) for it instead — see
 * `components/RecordEmptyScreen.jsx`'s own header for the render side.
 * Kept in this pure function (not inlined in the component) partly for
 * the same testability reason as the rest of this file, and partly
 * because it keeps `Dashboard.jsx`'s own cyclomatic complexity under the
 * lint budget — the boolean arithmetic lives here once, tested here once.
 *
 * Issue #761 — a FOURTH outcome, `showRecordOne`: the record after
 * exactly one match, same `!filterActive` scoping `showEmptyRecord` uses
 * (this slice does not touch the filtered case, which keeps reading
 * `isEmpty`/the plain "no matches for this filter" card exactly as
 * before). `matches === 1` is mutually exclusive with `isEmpty`
 * (`matches === 0`) by construction, so a caller can check `showEmptyRecord`
 * then `showRecordOne` in either order. THE HARD RULE THIS PRESERVES: a
 * record with two or more matches must look and behave exactly as today —
 * `showRecordOne` is false the moment `summary.matches !== 1`, and nothing
 * else in this function changes for that case.
 *
 * @param {object} args
 * @param {boolean} args.recordScreenOn — `newUi("record")`, read by the caller.
 * @param {{ matches: number } | null} args.summary — `/stats/summary`'s
 *   response, or `null` while it has not arrived (or failed) yet.
 * @param {boolean} args.filterActive — season/competition/team filter set.
 * @param {boolean} [args.summaryFailed] — the summary fetch failed (so
 *   `summary` is `null` but NOT pending — the existing #271 failure banner
 *   owns that state, untouched, and must not be mistaken for loading).
 * @returns {{ isEmpty: boolean, showEmptyRecord: boolean, recordLoading: boolean, showRecordOne: boolean }}
 */
export function computeRecordGate({ recordScreenOn, summary, filterActive, summaryFailed = false }) {
  const isEmpty = recordScreenOn
    ? !!summary && summary.matches === 0
    : filterActive && !!summary && summary.matches === 0;
  const showEmptyRecord = recordScreenOn && isEmpty && !filterActive;
  const recordLoading = recordScreenOn && !filterActive && summary === null && !summaryFailed;
  const showRecordOne = recordScreenOn && !filterActive && !!summary && summary.matches === 1;
  return { isEmpty, showEmptyRecord, recordLoading, showRecordOne };
}

/**
 * Issue #762 — "one record, not two stats areas", wave 3 slice 3. A FIFTH
 * outcome, kept as its own function rather than a fifth key on
 * `computeRecordGate` above: brief-762.md's own Step 0 says the season
 * record sits behind `newUi("season-record")` — a DIFFERENT screen id from
 * `newUi("record")` (#760/#761's own flag), per `lib/featureFlags.js`'s
 * `NEW_UI_SCREENS` list, which carries both as distinct entries. Folding a
 * fifth key into `computeRecordGate`'s return value would also break
 * `recordGate.test.js`'s own `toEqual({...four keys...})` assertions, which
 * pin that function's shape exactly — a second function is the change that
 * does not touch either contract.
 *
 * WHY THE GATING SIGNAL IS `matchesTotal`, NOT `summary.matches`. #760/#761
 * both scope their own outcome to `!filterActive`, because their gate can
 * only tell "genuinely empty" from "filtered to zero" by refusing to look
 * while a filter is on. The season record OWNS its filter bar (ruling 4)
 * and must show ITS OWN "filtered, no matches" state (ruling 3) rather than
 * falling back to the old dashboard's copy — so its entry gate cannot
 * depend on `filterActive` being false, and it cannot depend on
 * `/stats/summary` at all: that endpoint applies the CURRENT filter
 * server-side and exposes no raw balls-faced/overs/runs-conceded totals to
 * derive Strike Rate or Economy from independently (confirmed by reading
 * `api/src/routes/statsCompute.ts` — `computeStatsSummary` returns the
 * pre-divided `sr`/`econ` only). `matches.length` — `Dashboard.jsx`'s own
 * `GET /matches` state, always unfiltered, with every raw batting/bowling/
 * fielding count per match — is both the season-qualifying signal AND, via
 * `SeasonRecordScreen`'s own client-side season/competition/team filter,
 * the source every figure on that screen is computed from. `SeasonRecordScreen`
 * reads `matchesFailed` (that same `GET /matches` fetch failing) for its
 * OWN "career totals didn't load" banner (`dash-summary-error`) — a
 * SEPARATE concern from this gate, which only needs to know matches
 * COUNT, not whether the latest refetch succeeded.
 *
 * `seasonRecordLoading` mirrors `recordLoading`'s reasoning: nothing
 * populated-only may render before `matches` (the qualifying count AND the
 * figures' own source) has settled — brief-762 says to reuse #760's own
 * loading frame for this window.
 *
 * D149 (PR #880 review, Andreas) — `season-record` turns on only when
 * `record` is ALSO on: `recordScreenOn` (`newUi("record")`, the SAME value
 * `computeRecordGate` already takes) is now a required second flag here,
 * ANDed with `seasonRecordOn` (`newUi("season-record")`) into one internal
 * `seasonRecordActuallyOn`. With `season-record` on and `record` off (a
 * single-flag combination no e2e leg exercised before this ruling), the
 * result must be exactly today's flag-off behaviour — `recordGate.test.js`'s
 * own case for this pins it, watched failing against the PRE-D149 version
 * of this function (see that test's own header).
 */
/**
 * R1 (PR #880 review) — `matchesFailed`: a player who has already
 * qualified for the season record must not fall back to the shipped
 * dashboard just because THIS refetch of `GET /matches` failed (`matches`
 * itself stays at its last successful value or `[]` on a fresh mount —
 * either way `matchesTotal` can no longer be trusted once the fetch that
 * produces it has failed). `showSeasonRecord` fires on a failure
 * regardless of the (unreliable) count, so `SeasonRecordScreen` owns
 * showing its own failure state — never a silent fall-through to
 * `Dashboard.jsx`'s old chrome.
 */
export function computeSeasonRecordGate({ seasonRecordOn, recordScreenOn, matchesTotal, matchesLoaded, matchesFailed = false }) {
  const seasonRecordActuallyOn = seasonRecordOn && recordScreenOn;
  const seasonRecordLoading = seasonRecordActuallyOn && !matchesLoaded;
  const showSeasonRecord = seasonRecordActuallyOn && !seasonRecordLoading && (matchesFailed || matchesTotal >= 2);
  return { seasonRecordLoading, showSeasonRecord };
}

/**
 * Issue #1217 — the record's own failed-to-load states. A player with 0 or
 * 1 matches whose `GET /stats/summary` failed (after its bounded retries)
 * matched NONE of the gates above: `computeRecordGate`'s `isEmpty`,
 * `showEmptyRecord` and `showRecordOne` all need `summary` non-null, and
 * `computeSeasonRecordGate` needs two or more matches. So the page fell
 * through to the shipped dashboard (old sidebar shell, slate
 * `dash-summary-error` card, four bento tiles) on the one screen a player
 * is most likely to be looking at on a bad connection.
 *
 * Round 5 drew both states and Martin signed them off (D118):
 * `EmptyRecord.dc.html:102-104` ("state: failed to load · When the record
 * could not be read · not the same as empty") for 0 matches, and
 * `OneInnings.dc.html:136-138` ("state: failed to load · When the totals
 * could not be read · the figures show a dash, never a nought") for 1.
 *
 * Another function rather than a fifth key on `computeRecordGate`, for the
 * reason `computeSeasonRecordGate`'s header gives: `recordGate.test.js` pins
 * that function's four-key shape with `toEqual`. `computeRecordGate` and
 * `computeSeasonRecordGate` are unchanged, so the flag-off leg is too.
 *
 * The count comes from `matchesTotal` (`GET /matches`, unfiltered), since
 * `summary` is exactly what is missing. `matchesFailed` is excluded: with
 * both reads down the count cannot be trusted, and that case is already the
 * season record's own failure state (R1, `computeSeasonRecordGate`), which
 * must not change.
 *
 * @param {object} args
 * @param {boolean} args.recordScreenOn — `newUi("record")`.
 * @param {boolean} args.filterActive — season/competition/team filter set.
 * @param {boolean} args.summaryFailed — the summary fetch gave up.
 * @param {boolean} args.matchesLoaded — `GET /matches` has settled.
 * @param {boolean} [args.matchesFailed] — `GET /matches` failed.
 * @param {number} args.matchesTotal — `matches.length`.
 * @returns {{ recordFailedEmpty: boolean, recordFailedOne: boolean }}
 */
export function computeRecordFailedGate({ recordScreenOn, filterActive, summaryFailed, matchesLoaded, matchesFailed = false, matchesTotal }) {
  const base = recordScreenOn && !filterActive && summaryFailed && matchesLoaded && !matchesFailed;
  return {
    recordFailedEmpty: base && matchesTotal === 0,
    recordFailedOne: base && matchesTotal === 1,
  };
}
